(() => {
  'use strict';
  const prayers = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const allTimes = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
  const storageKey = 'ii-salah-preferences-v1';
  const normalise = s => String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]+/g, ' ').trim();
  const localDate = (now, zone) => new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const dayDate = key => { const [y, m, d] = key.split('-').map(Number); return new Date(y, m - 1, d, 12); };
  const nextDate = key => { const d = dayDate(key); d.setDate(d.getDate() + 1); return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-'); };
  const validZone = zone => { try { new Intl.DateTimeFormat('en', { timeZone: zone }).format(); return true; } catch { return false; } };
  let cityData;
  let cityPromise;
  class IISalahTimes extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.el = key => this.querySelector(`[data-${key}]`);
      this.location = { name: 'London', country: 'United Kingdom', lat: 51.50853, lon: -0.12574, zone: 'Europe/London' };
      this.followToday = true;
      this.form = this.querySelector('form');
      this.lib = window.IISalahLibrary;
      if (!this.lib) { this.error(this.copy.library_error); return; }
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey));
        if (saved?.location && this.validLocation(saved.location)) {
          this.location = saved.location;
          for (const name of ['method', 'asr', 'format']) {
            const field = this.form.elements[name];
            if ([...field.options].some(o => o.value === saved[name])) field.value = saved[name];
          }
          this.form.elements.remember.checked = true;
        }
      } catch { /* Storage can be unavailable in private browsing. */ }
      this.syncLocation();
      this.form.elements.date.value = localDate(new Date(), this.location.zone);
      this.form.addEventListener('submit', e => { e.preventDefault(); this.findCities(); });
      this.form.elements.city.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.findCities(), 220);
      });
      this.form.elements.city.addEventListener('keydown', e => {
        if (e.key === 'Escape') { this.searchToken = (this.searchToken || 0) + 1; clearTimeout(this.searchTimer); this.el('results').replaceChildren(); this.el('search-status').textContent = ''; }
      });
      this.el('results').addEventListener('click', e => {
        const button = e.target.closest('button[data-city-index]');
        if (!button) return;
        const row = cityData.cities[Number(button.dataset.cityIndex)];
        this.location = { name: row[0], country: cityData.country[row[1]], region: row[2], lat: row[3], lon: row[4], zone: row[5] };
        this.el('results').replaceChildren();
        this.form.elements.city.value = '';
        this.el('search-status').textContent = this.copy.selected + ' ' + this.location.name;
        this.followToday = true;
        this.syncLocation();
        this.form.elements.date.value = localDate(new Date(), this.location.zone);
        this.render();
        this.el('location').focus({ preventScroll: true });
      });
      this.form.addEventListener('change', e => {
        if (e.target.name === 'date') this.followToday = e.target.value === localDate(new Date(), this.location.zone);
        if (e.target.name !== 'city') this.render();
      });
      this.el('today').addEventListener('click', () => {
        this.followToday = true;
        this.form.elements.date.value = localDate(new Date(), this.location.zone);
        this.render();
      });
      this.el('locate').addEventListener('click', () => this.locate());
      this.el('coordinates-apply').addEventListener('click', () => this.applyCoordinates());
      this.el('forget').addEventListener('click', () => {
        try { localStorage.removeItem(storageKey); } catch {}
        this.form.elements.remember.checked = false;
        this.el('status').textContent = this.copy.forgotten;
      });
      this.render();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); clearTimeout(this.searchTimer); }
    validLocation(l) { return typeof l.name === 'string' && Number.isFinite(l.lat) && Math.abs(l.lat) <= 90 && Number.isFinite(l.lon) && Math.abs(l.lon) <= 180 && validZone(l.zone); }
    syncLocation() {
      this.el('location').textContent = [this.location.name, this.location.country].filter(Boolean).join(', ');
      this.form.elements.latitude.value = this.location.lat;
      this.form.elements.longitude.value = this.location.lon;
      this.form.elements.timezone.value = this.location.zone;
    }
    async loadCities() {
      if (cityData) return cityData;
      if (!cityPromise) cityPromise = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(this.dataset.citiesUrl, { signal: controller.signal, credentials: 'omit' });
          if (!response.ok) throw Error('City data unavailable');
          const data = await response.json();
          if (!Array.isArray(data.cities) || !data.country || data.cities.length < 1000) throw Error('Invalid city data');
          data.search = data.cities.map(r => normalise([r[0], r[1], r[2], data.country[r[1]], r[7], r[1] === 'GB' ? 'UK' : '', r[1] === 'US' ? 'USA' : ''].join(' ')));
          cityData = data;
          return data;
        } finally { clearTimeout(timer); }
      })().catch(e => { cityPromise = null; throw e; });
      return cityPromise;
    }
    async findCities() {
      const query = normalise(this.form.elements.city.value);
      const token = this.searchToken = (this.searchToken || 0) + 1;
      this.el('results').replaceChildren();
      if (query.length < 2) { this.el('search-status').textContent = ''; return; }
      this.el('search-status').textContent = this.copy.loading_cities;
      try {
        const data = await this.loadCities();
        if (token !== this.searchToken || !this.isConnected) return;
        const words = query.split(' ');
        const matches = [];
        for (let i = 0; i < data.cities.length && matches.length < 8; i++) {
          if (words.every(w => data.search[i].includes(w))) matches.push(i);
        }
        this.el('search-status').textContent = matches.length ? this.copy.choose_match : this.copy.no_match;
        for (const i of matches) {
          const r = data.cities[i];
          const li = document.createElement('li');
          const b = document.createElement('button'); b.type = 'button'; b.dataset.cityIndex = i;
          b.textContent = [r[0], r[2], data.country[r[1]]].filter(Boolean).join(', ');
          li.append(b); this.el('results').append(li);
        }
      } catch { if (token === this.searchToken) this.el('search-status').textContent = this.copy.city_error; }
    }
    locate() {
      if (!navigator.geolocation) { this.el('status').textContent = this.copy.location_error; return; }
      this.el('locate').disabled = true;
      this.el('status').textContent = this.copy.locating;
      navigator.geolocation.getCurrentPosition(position => {
        this.el('locate').disabled = false;
        const lat = Number(position.coords.latitude.toFixed(4));
        const lon = Number(position.coords.longitude.toFixed(4));
        try {
          this.form.elements.latitude.value = lat;
          this.form.elements.longitude.value = lon;
          this.form.elements.timezone.value = this.lib.timezone(lat, lon);
          this.el('settings').open = true;
          this.el('coordinates').open = true;
          this.el('status').textContent = this.copy.confirm_location;
          this.form.elements.timezone.focus();
        } catch { this.el('status').textContent = this.copy.location_error; }
      }, () => { this.el('locate').disabled = false; this.el('status').textContent = this.copy.location_error; }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 });
    }
    applyCoordinates() {
      const latInput = this.form.elements.latitude;
      const lonInput = this.form.elements.longitude;
      const tzInput = this.form.elements.timezone;
      const loc = { name: this.copy.your_location, country: '', lat: Number(latInput.value), lon: Number(lonInput.value), zone: tzInput.value.trim() };
      if (!latInput.value || !lonInput.value || !this.validLocation(loc)) { this.el('status').textContent = this.copy.invalid_coordinates; return; }
      this.location = loc; this.followToday = true; this.syncLocation();
      this.form.elements.date.value = localDate(new Date(), loc.zone);
      this.el('coordinates').open = false; this.render();
    }
    isLondon() {
      return this.location.name === 'London' && this.location.country === 'United Kingdom' && this.location.zone === 'Europe/London' && Math.abs(this.location.lat - 51.50853) < 0.01 && Math.abs(this.location.lon + 0.12574) < 0.01;
    }
    method() {
      const chosen = this.form.elements.method.value;
      // London's calculated default is local to the named city, not a global offset.
      return chosen === 'Local' ? (this.isLondon() ? 'MoonsightingCommittee' : 'MuslimWorldLeague') : chosen;
    }
    parameters(dateKey) {
      const L = this.lib;
      const method = this.method();
      const p = L.CalculationMethod[method]();
      p.madhab = this.form.elements.asr.value === 'hanafi' ? L.Madhab.Hanafi : L.Madhab.Shafi;
      p.highLatitudeRule = L.HighLatitudeRule.TwilightAngle;
      p.polarCircleResolution = L.PolarCircleResolution.Unresolved;
      // Umm al-Qura's Ramadan Isha interval is explicitly documented by Adhan.js.
      if (method === 'UmmAlQura') {
        const month = new Intl.DateTimeFormat('en', { calendar: 'islamic-umalqura', month: 'numeric' }).format(dayDate(dateKey));
        if (Number(month) === 9) p.adjustments.isha = 30;
      }
      return p;
    }
    calculate(key) {
      return new this.lib.PrayerTimes(new this.lib.Coordinates(this.location.lat, this.location.lon), dayDate(key), this.parameters(key));
    }
    time(date) {
      if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return '--:--';
      return new Intl.DateTimeFormat('en-GB', { timeZone: this.location.zone, hour: '2-digit', minute: '2-digit', hour12: this.form.elements.format.value === '12' }).format(date);
    }
    error(text) {
      this.current = null;
      this.el('status').textContent = text;
      for (const key of allTimes) this.querySelector(`[data-time="${key}"]`).textContent = '--:--';
      this.el('next-name').textContent = this.copy.unavailable;
      this.el('next-countdown').textContent = '';
    }
    render() {
      const key = this.form.elements.date.value;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key) || !this.form.elements.date.checkValidity()) { this.error(this.copy.invalid_date); return; }
      try {
        this.current = this.calculate(key);
        this.nextDay = this.calculate(nextDate(key));
        for (const name of allTimes) this.querySelector(`[data-time="${name}"]`).textContent = this.time(this.current[name]);
        const missing = allTimes.some(name => !Number.isFinite(this.current[name]?.getTime()));
        this.el('status').textContent = missing ? this.copy.polar_unavailable : this.copy.calculated;
        const methodText = [...this.form.elements.method.options].find(o => o.value === this.method()).textContent;
        this.el('active-settings').textContent = `${this.copy.calculated_label} · ${methodText} · ${this.form.elements.asr.value === 'hanafi' ? this.copy.asr_short_hanafi : this.copy.asr_short_standard}`;
        this.el('london-reference').hidden = !this.isLondon();
        this.el('zone').textContent = this.location.zone + ' · ' + new Intl.DateTimeFormat('en-GB', { timeZone: this.location.zone, timeZoneName: 'short' }).formatToParts(this.current.dhuhr).find(p => p.type === 'timeZoneName')?.value;
        this.el('date-heading').textContent = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).format(dayDate(key));
        if (this.form.elements.remember.checked) {
          const record = { location: this.location };
          for (const n of ['method', 'asr', 'format']) record[n] = this.form.elements[n].value;
          try { localStorage.setItem(storageKey, JSON.stringify(record)); } catch { this.el('status').textContent += ' ' + this.copy.storage_error; }
        } else { try { localStorage.removeItem(storageKey); } catch {} }
        this.dataset.renderedDate = key;
        this.tick();
      } catch { this.error(this.copy.polar_unavailable); }
    }
    tick() {
      const now = new Date();
      this.el('clock').textContent = this.time(now);
      if (!this.current) return;
      const today = localDate(now, this.location.zone);
      if (this.followToday && this.form.elements.date.value !== today) { this.form.elements.date.value = today; this.render(); return; }
      if (this.form.elements.date.value !== today) { this.el('next-name').textContent = this.copy.selected_date; this.el('next-countdown').textContent = ''; return; }
      const candidates = [this.current, this.nextDay].flatMap(day => prayers.map(name => ({ name, at: day[name] }))).filter(p => Number.isFinite(p.at?.getTime()) && p.at > now).sort((a,b) => a.at - b.at);
      const next = candidates[0];
      this.querySelectorAll('[data-prayer]').forEach(n => n.classList.toggle('is-next', n.dataset.prayer === next?.name));
      if (!next) { this.el('next-name').textContent = this.copy.unavailable; this.el('next-countdown').textContent = ''; return; }
      const seconds = Math.max(0, Math.ceil((next.at - now) / 1000));
      this.el('next-name').textContent = this.copy[next.name] + ' · ' + this.time(next.at);
      this.el('next-countdown').textContent = [Math.floor(seconds/3600), Math.floor(seconds/60)%60, seconds%60].map(n => String(n).padStart(2,'0')).join(':');
    }
  }
  if (!customElements.get('ii-salah-times')) customElements.define('ii-salah-times', IISalahTimes);
})();
